import { useEffect, useState } from "react";
import { Image as ImageIcon } from "lucide-react";

import { imageSrc } from "@/lib/image-src";
import { cn } from "@/lib/utils";

// Renders a product image with a fallback chain: the customer's origin URL
// first (available immediately after ingest), then our stored copy through
// the image proxy (survives dead links / hotlink blocks), then a placeholder.
type ImageLike = {
  url?: string | null;
  storage_url?: string | null;
  storageUrl?: string | null;
};

export function CatalogueImage({
  image,
  width,
  alt,
  className,
}: {
  image: ImageLike | null | undefined;
  width?: number;
  alt: string;
  className?: string;
}) {
  const stored = image?.storage_url ?? image?.storageUrl;
  const candidates = [
    imageSrc(image?.url, { width }),
    imageSrc(stored, { width }),
  ].filter((src, i, all): src is string => !!src && all.indexOf(src) === i);

  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx(0), [image?.url, stored]);

  if (idx >= candidates.length) {
    return (
      <div
        role="img"
        aria-label={`${alt} — image unavailable`}
        className={cn(
          "flex items-center justify-center bg-muted/50 text-muted-foreground/50",
          className,
        )}
      >
        <ImageIcon aria-hidden="true" className="h-8 w-8" />
      </div>
    );
  }

  return (
    <img
      src={candidates[idx]}
      alt={alt}
      className={className}
      onError={() => setIdx((i) => i + 1)}
    />
  );
}
